import { Box, Button, Link, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { StoryPageLayout } from '../layouts';

const PAGE_URL = 'https://www.rsmc.ai/updates/rochelle-manns-milestone-circles';
const PAGE_TITLE =
  'Rochelle Manns Accepted into Milestone Circles at the Nasdaq Entrepreneurial Center';
const PAGE_DESCRIPTION =
  'RSMC founder Rochelle Manns joins Milestone Circles at the Nasdaq Entrepreneurial Center to grow her mission of helping small and midsize organizations adopt cloud and AI.';
const PROGRAM_URL = 'https://nasdaqcenter.org/milestone-circles/';
const LINKEDIN_URL = 'https://www.linkedin.com/company/rsm-collective/';

// Common paragraph styling
const paragraphSx = {
  fontSize: '1rem',
  lineHeight: 1.8,
  color: 'var(--color-text)',
  mb: 3,
};

// Section heading styling
const headingSx = {
  fontWeight: 700,
  mb: 3,
  mt: 6,
  color: 'var(--color-heading)',
  fontSize: { xs: '1.25rem', md: '1.5rem' },
};

// Link styling
const linkSx = {
  color: 'var(--color-primary)',
  textDecoration: 'underline',
};

const programStats = [
  { value: '12 weeks', label: 'of focused, milestone-driven growth' },
  { value: '12 founders', label: "in Rochelle's Circle" },
  {
    value: '1 Milestone',
    label:
      'increase adoption of HubLife™, agentic operations and productivity platform for mission-driven, small and medium sized organizations',
    // The long label gets a wider card: full row on phones, double width above
    wide: true,
  },
];

/**
 * Sidebar prompt above the subscribe form, pointing readers to LinkedIn for
 * the week 6 and week 12 progress updates.
 *
 * @returns {import('react').ReactElement} Follow-along call to action
 */
function FollowAlong() {
  return (
    <Box
      sx={{
        border: '1px solid var(--color-border)',
        borderLeft: '4px solid var(--color-primary)',
        borderRadius: '8px',
        backgroundColor: 'var(--color-card)',
        p: 3,
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          fontSize: '1.125rem',
          color: 'var(--color-heading)',
          mb: 1,
        }}
      >
        Follow the 12-week journey
      </Typography>
      <Typography
        sx={{
          fontSize: '0.9375rem',
          lineHeight: 1.6,
          color: 'var(--color-text-muted)',
          mb: 2.5,
        }}
      >
        Progress updates at week 6 and graduation at week 12.
      </Typography>
      <Button
        variant="contained"
        href={LINKEDIN_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Follow RSMC on LinkedIn (opens in new window)"
        sx={{
          backgroundColor: 'primary.main',
          color: 'var(--color-primary-text)',
          fontWeight: 600,
          textTransform: 'none',
          '&:hover': { color: 'var(--color-primary-text)' },
        }}
      >
        Follow RSMC on LinkedIn
      </Button>
    </Box>
  );
}

/**
 * Founder news: Rochelle Manns joins the Nasdaq Entrepreneurial Center's
 * Milestone Circles 2026-2027 cohort. Uses StoryPageLayout like the other
 * articles, with the subscribe form in the sidebar so readers can follow the
 * 12-week program.
 *
 * @returns {import('react').ReactElement} Milestone Circles update page
 */
function MilestoneCirclesUpdate() {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    author: {
      '@type': 'Organization',
      name: 'Ready Secure Managed Cloud',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Ready Secure Managed Cloud',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.rsmc.ai/images/r-logo.png',
      },
    },
    datePublished: '2026-10-07',
    dateModified: '2026-10-07',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': PAGE_URL,
    },
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE} | RSMC</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content="https://www.rsmc.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://www.rsmc.ai/og-image.png" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta
          name="keywords"
          content="Rochelle Manns, Milestone Circles, Nasdaq Entrepreneurial Center, Wells Fargo Foundation, founder, HubLife, RSMC, cloud and AI"
        />
        <link rel="canonical" href={PAGE_URL} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <StoryPageLayout
        title="Founder News"
        subtitle="Milestone Circles at the Nasdaq Entrepreneurial Center"
        formHeading="Get RSMC Updates"
        formFilePath="/forms/subscribe-form.txt"
        sidebarContent={<FollowAlong />}
        errorMessage="Unable to load subscribe form"
        loadingMessage="Loading subscribe form..."
      >
        <Box component="article">
          {/* Article Title */}
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              color: 'var(--color-heading)',
              mb: 3,
            }}
          >
            {PAGE_TITLE}
          </Typography>

          {/* Editorial meta row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 4,
            }}
          >
            {['Ready Secure Managed Cloud', 'October 2026', '3 min read'].map(
              (item, index) => (
                <Box
                  key={item}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                  {index > 0 && (
                    <Box
                      component="span"
                      aria-hidden="true"
                      sx={{
                        width: 4,
                        height: 4,
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-primary)',
                      }}
                    />
                  )}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      letterSpacing: '0.5px',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              )
            )}
          </Box>

          {/* Deck */}
          <Typography
            sx={{
              ...paragraphSx,
              fontSize: '1.125rem',
              color: 'var(--color-text-muted)',
              mb: 4,
            }}
          >
            RSMC&rsquo;s founder joins the 2026-2027 cohort of a free, 12-week
            program that helps U.S.-based founders build, scale, and lead with
            purpose.
          </Typography>

          {/* Program at a glance */}
          <Box
            component="ul"
            aria-label="Program at a glance"
            sx={{
              listStyle: 'none',
              p: 0,
              mt: 0,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 2fr' },
              gap: 2,
              mb: 5,
            }}
          >
            {programStats.map((stat) => (
              <Box
                key={stat.value}
                component="li"
                sx={{
                  gridColumn: stat.wide ? { xs: '1 / -1', sm: 'auto' } : 'auto',
                  border: '1px solid var(--color-border)',
                  borderTop: '3px solid var(--color-primary)',
                  borderRadius: '8px',
                  backgroundColor: 'var(--color-card)',
                  p: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '1.125rem', md: '1.25rem' },
                    color: 'var(--color-heading)',
                    mb: 0.5,
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    lineHeight: 1.5,
                    color: 'var(--color-text-muted)',
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography sx={paragraphSx}>
            Rochelle Manns, founder of Ready Secure Managed Cloud (RSMC), has
            been accepted into the 2026-2027{' '}
            <Link
              href={PROGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={linkSx}
            >
              Milestone Circles
            </Link>{' '}
            program at the Nasdaq Entrepreneurial Center. Milestone Circles is a
            free, 12-week program that helps U.S.-based founders build, scale,
            and lead with purpose. It is made possible by the generous support
            of the Wells Fargo Foundation.
          </Typography>

          <Typography variant="h2" sx={headingSx}>
            Why Rochelle Founded RSMC
          </Typography>

          <Typography sx={paragraphSx}>
            After years of helping enterprise clients accelerate growth and
            transform business operations, Rochelle founded Ready Secure Managed
            Cloud to help mission-driven, small, and midsize organizations adopt
            cloud and AI. RSMC brings enterprise-level cloud and AI expertise to
            nonprofits, education, life sciences, and growing businesses.
          </Typography>

          <Typography variant="h2" sx={headingSx}>
            A Program Built for Founders
          </Typography>

          <Typography sx={paragraphSx}>
            Over the past five years, Milestone Circles has supported more than
            6,300 entrepreneurs. Each founder chooses one milestone: an
            important goal that signals the business is moving to its next stage
            of growth.
          </Typography>

          <Typography sx={paragraphSx}>
            Rochelle will join a Circle of 12 founders. Together, they will work
            through 12 weeks of live workshops led by experts, mentorship from
            experienced business leaders, and accountability tools that track
            progress toward each milestone. Participants also have access to
            on-demand classes, workbooks, and toolkits.
          </Typography>

          <Typography variant="h2" sx={headingSx}>
            Rochelle&rsquo;s Milestone: Helping Organizations Own Their AI
            Journey
          </Typography>

          {/* Milestone callout */}
          <Box
            sx={{
              border: '1px solid var(--color-border)',
              borderLeft: '4px solid var(--color-primary)',
              borderRadius: '8px',
              backgroundColor: 'var(--color-card)',
              p: { xs: 2.5, md: 3 },
              mb: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: 'var(--color-primary)',
                mb: 1,
              }}
            >
              The 12-week milestone
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: '1.125rem',
                color: 'var(--color-heading)',
                mb: 1.5,
              }}
            >
              Growing adoption of HubLife
            </Typography>
            <Typography sx={{ ...paragraphSx, mb: 0 }}>
              Over the next 12 weeks, Rochelle will focus on growing adoption of{' '}
              <strong>HubLife</strong>, RSMC&rsquo;s agentic operations and AI
              productivity solution. HubLife enables secure, financially
              sustainable adoption of cloud and AI, so mission-driven, small,
              and midsize organizations can own their AI journey.
            </Typography>
          </Box>

          {/* Founder quote */}
          <Box
            component="figure"
            sx={{
              my: { xs: 6, md: 7 },
              mx: 0,
              pl: { xs: 1, md: 6 },
              pr: { xs: 1, md: 2 },
            }}
          >
            <Typography
              component="span"
              aria-hidden="true"
              sx={{
                display: 'block',
                fontFamily: '"Playfair Display", serif',
                fontSize: '4rem',
                lineHeight: 0.6,
                color: 'var(--color-primary)',
                mb: 2,
              }}
            >
              &ldquo;
            </Typography>
            <Typography
              component="blockquote"
              sx={{
                fontSize: { xs: '1.125rem', md: '1.3125rem' },
                lineHeight: 1.55,
                fontWeight: 600,
                color: 'var(--color-heading)',
                fontStyle: 'italic',
                m: 0,
                mb: 2,
              }}
            >
              I started RSMC to help mission-driven, small, and midsize
              organizations adopt cloud and AI. Milestone Circles gives me the
              structure and the founder community to grow that mission with
              focus. I&rsquo;m grateful to the Nasdaq Entrepreneurial Center and
              the Wells Fargo Foundation for making it possible.
            </Typography>
            <Typography
              component="figcaption"
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--color-text-muted)',
              }}
            >
              Rochelle Manns, Founder, Ready Secure Managed Cloud
            </Typography>
          </Box>

          <Typography variant="h2" sx={headingSx}>
            Follow Along
          </Typography>

          <Typography sx={paragraphSx}>
            RSMC will share updates and lessons from Rochelle&rsquo;s Circle
            over the next 12 weeks. Learn more about the program at{' '}
            <Link
              href={PROGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={linkSx}
            >
              nasdaqcenter.org/milestone-circles
            </Link>
            .
          </Typography>

          {/* Visual separator - R logo */}
          <Box sx={{ textAlign: 'center', mt: 6, mb: 2 }}>
            <Box
              component="img"
              src="/images/r-logo.png"
              alt="Ready Secure Managed Cloud"
              sx={{ width: '50px', height: 'auto', opacity: 0.7 }}
            />
          </Box>
        </Box>
      </StoryPageLayout>
    </>
  );
}

export default MilestoneCirclesUpdate;
